import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ArrowUpDown, Settings, Filter, RefreshCw, X, Box
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const ToolList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate } = usePermissions('tools'); // الصلاحيات

  // --- State ---
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  // --- URL Params (Persistence) ---
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const toolnum = searchParams.get('toolnum') || "";
  const description = searchParams.get('description') || "";
  const tooltype = searchParams.get('tooltype') || "";
  const status = searchParams.get('status') || "";
  const sortBy = searchParams.get('sortBy') || "toolnum";
  const sortDir = searchParams.get('sortDir') || "asc";

  const fetchTools = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/tools', {
        params: { page, limit, search: q, toolnum, description, tooltype, status, sortBy, sortDir }
      });
      setTools(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(t('common.load_error', "Error loading tools"));
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, toolnum, description, tooltype, status, sortBy, sortDir, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchTools(), 100);
    return () => clearTimeout(timer);
  }, [fetchTools]);

  // --- Filter Handlers ---
  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  const handleClearFilters = () => {
    setSearchParams({});
  };

  const handleRowClick = (id) => {
    const queryParams = new URLSearchParams(Object.fromEntries(searchParams.entries())).toString();
    navigate(`/tools/${id}${queryParams ? '?' + queryParams : ''}`);
  };

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} dir={isRTL ? 'rtl' : 'ltr'} style={{ fontSize: `${fontSize}px` }}>

      {/* Header */}
      <div className="bg-surface-card p-3 border-b flex justify-between items-center shadow-sm z-20">
        <div className={`flex items-center gap-4 ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <h1 className="text-primary font-black italic text-lg flex items-center gap-2">
            <Settings size={24} className="text-status-warning" /> {t('tools.list.title', 'Tool Master')}
          </h1>
        </div>

        <div className={`flex gap-3 items-center ${isRTL ? 'ml-4' : 'mr-4'}`}>
          <button onClick={handleClearFilters} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
            <X size={16}/> {t('common.clear_filters', 'CLEAR FILTERS')}
          </button>
          <button onClick={() => fetchTools()} className="p-2 text-ink-secondary hover:text-primary transition-colors" title="Refresh">
            <RefreshCw size={16} className={loading ? "animate-spin text-status-warning" : ""} />
          </button>
          {canCreate && (
            <button onClick={() => navigate('/tools/new')} className="bg-primary text-white px-6 py-2 rounded font-black hover:bg-primary-dark shadow-md transition-all active:scale-95 flex items-center gap-2">
              <Plus size={14}/> {t('tools.list.new', 'NEW TOOL')}
            </button>
          )}
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border shadow-sm rounded-sm overflow-hidden min-h-[450px]">
          <table className="w-full text-left border-collapse">
            <thead className="bg-surface-panel border-b text-ink font-black uppercase text-[0.9em] sticky top-0 z-10 tracking-widest">
              <tr>
                <SortableHeader label={t('tools.fields.toolnum', 'Tool Number')} sortKey="toolnum" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('assets.headers.desc', 'Description')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <SortableHeader label={t('tools.fields.type', 'Type')} sortKey="tooltype" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <SortableHeader label={t('assets.headers.status', 'Status')} sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} />
                <th className="p-3 border-x border-line text-center">{t('tools.fields.is_stocked', 'Is Stocked?')}</th>
              </tr>
              {/* Filter Row */}
              <tr className="bg-surface-card border-b border-line shadow-sm">
                <td className="p-1 border-x border-line-subtle">
                  <div className={`relative flex items-center `}>
                    <Search className={`absolute text-ink-secondary ${isRTL ? 'right-2' : 'left-2'}`} size={12} />
                    <FilterInput value={toolnum} onChange={v => updateFilters({ toolnum: v, page: 1 })} className={isRTL ? 'pr-6' : 'pl-6'}/>
                  </div>
                </td>
                <td className="p-1 border-x border-line-subtle"><FilterInput value={description} onChange={v => updateFilters({ description: v, page: 1 })} /></td>
                <td className="p-1 border-x border-line-subtle bg-surface-panel/30">
                  <select className={`w-full p-1.5 text-primary font-bold outline-none bg-transparent cursor-pointer ${isRTL ? 'text-right' : 'text-center'}`} value={tooltype} onChange={e => updateFilters({ tooltype: e.target.value, page: 1 })}>
                    <option value="">{t('common.all', 'ALL')}</option>
                    <option value="HAND">HAND</option>
                    <option value="POWER">POWER</option>
                    <option value="MEASURING">MEASURING</option>
                  </select>
                </td>
                <td className="p-1 border-x border-line-subtle bg-surface-panel/30">
                  <select className={`w-full p-1.5 text-primary font-bold outline-none bg-transparent cursor-pointer ${isRTL ? 'text-right' : 'text-center'}`} value={status} onChange={e => updateFilters({ status: e.target.value, page: 1 })}>
                    <option value="">{t('common.all', 'ALL')}</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </td>
                <td className="p-1 border-x border-line-subtle bg-surface-panel/30"></td>
              </tr>
            </thead>

            <tbody className="divide-y divide-line-subtle font-bold">
              {loading ? (
                <tr><td colSpan="5" className="p-20 text-center animate-pulse font-black text-ink-secondary uppercase tracking-[0.5em]">{t('common.syncing', 'Syncing Data...')}</td></tr>
              ) : tools.length === 0 ? (
                <tr><td colSpan="5" className="p-20 text-center text-ink-secondary italic font-black uppercase tracking-widest">{t('common.no_records', 'No records found')}</td></tr>
              ) : tools.map((tool) => (
                <tr
                  key={tool.toolnum}
                  onClick={() => handleRowClick(tool.toolnum)}
                  className={`hover:bg-primary-soft/50 cursor-pointer transition-all group ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  <td className="p-3 border-x border-line-subtle font-black text-primary group-hover:underline">{tool.toolnum}</td>
                  <td className="p-3 border-x border-line-subtle text-ink truncate max-w-xs">{tool.description}</td>
                  <td className="p-3 border-x border-line-subtle text-center text-ink-secondary">{tool.tooltype || '---'}</td>
                  <td className="p-3 border-x border-line-subtle text-center">
                    {/* Simple ACTIVE/else switch — not moved to shared StatusBadge because
                        StatusBadge has no ACTIVE-vs-everything-else rule (INACTIVE would
                        render neutral gray there, not red as intended here). */}
                    <span className={`px-3 py-1 rounded text-[0.9em] font-black shadow-sm ${tool.status === 'ACTIVE' ? 'bg-status-success-bg text-status-success' : 'bg-status-danger-bg text-status-danger'}`}>
                      {tool.status}
                    </span>
                  </td>
                  <td className="p-3 border-x border-line-subtle text-center text-lg">{tool.is_stocked ? '✅' : '❌'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Footer */}
      <div className={`bg-surface-card border-t border-line p-2 px-8 flex justify-between items-center shadow-sm z-30 font-black text-[10px] text-ink-secondary `}>
        <div className={`flex items-center gap-6 `}>
            <div className={`flex items-center gap-2 `}>
                <Box size={14} className="text-primary"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-primary">{(page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-status-warning"/>
                <span>{t('common.total', 'Total')}: <span className="text-status-warning">{total}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 `}>
            <div className={`flex items-center gap-1 bg-surface-panel rounded px-2 py-1 border border-line `}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <div className="px-4 text-[11px] font-mono border-x border-line mx-1">
                    <span className="text-primary text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL }) => (
  <th className={`p-3 border-x border-line cursor-pointer hover:bg-primary-soft transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : ''} `}>
          {label}
          <ArrowUpDown size={12} className={sortBy === sortKey ? 'text-primary' : 'text-ink-secondary'} />
      </div>
  </th>
);

const FilterInput = ({ value, onChange, placeholder, center = false, className = "" }) => (
  <input
    className={`w-full p-1.5 text-primary font-bold outline-none bg-transparent placeholder-ink-secondary ${center ? 'text-center' : ''} ${className}`}
    placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)}
  />
);

export default ToolList;
